import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import moment from "moment";
import { useUser } from "util/context/user_context";
import { getMatchingList } from "firebase_/firebase_function/matching";
import { getCommunityData } from "firebase_/firebase_function/firebase_community";
import { getBlockData } from "firebase_/firebase_function/firebase_user";
import AdBannerSlot from "components/main/ad_banner_slot";
import Chatbot from "components/chatbot";

// ==== 디자인 토큰 ====
const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#F3F4F6",
  bg: "#F7FAF7",
  amber100: "#FEF3C7",
  amber800: "#92400E",
};

function StatusChip({ status }) {
  const map = {
    0: { label: "매칭중", bg: C.amber100, fg: C.amber800, dot: "#F59E0B" },
    1: { label: "매칭완료", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
    2: { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.green },
  };
  const s = map[status] ?? map[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 9999,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: 9999,
          background: s.dot,
          flexShrink: 0,
        }}
      />
      {s.label}
    </span>
  );
}

const categoryEmoji = (name = "") => {
  const s = String(name);
  if (s.includes("트랙터")) return "🚜";
  if (s.includes("콤바인")) return "🌾";
  if (s.includes("제어기") || s.includes("전자") || s.includes("전기")) return "⚡";
  if (s.includes("엔진") || s.includes("모터")) return "⚙️";
  if (s.includes("배터리")) return "🔋";
  if (s.includes("오일") || s.includes("연료")) return "🛢️";
  if (s.includes("이앙기") || s.includes("파종")) return "🌱";
  if (s.includes("바퀴") || s.includes("타이어")) return "⚫";
  return "🔧";
};

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YY.MM.DD");
};

function MatchingCardMobile({ item, to }) {
  const names = item?.categoryListName ?? [];
  const primary = names[0] || "기타";
  const secondary = names.slice(1).filter((n) => n && n !== "기타").join(" · ");
  const emoji = categoryEmoji(primary);
  const imageCount = Array.isArray(item.imageUrls) ? item.imageUrls.length : 0;
  const firstImage = imageCount > 0 ? item.imageUrls[0] : null;
  const expertName = item.expertName?.trim();
  const statusStr = item.status;

  return (
    <NavLink
      to={to}
      style={{
        display: "flex",
        gap: 12,
        padding: 12,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        textDecoration: "none",
      }}
    >
      {/* Thumb */}
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 10,
          background: firstImage ? "#F3F4F6" : C.greenBg,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {firstImage ? (
          <img
            src={firstImage}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span>{emoji}</span>
        )}
        {imageCount > 1 && (
          <span
            style={{
              position: "absolute",
              bottom: 2,
              right: 2,
              background: "rgba(17,24,39,0.75)",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              padding: "1px 5px",
              borderRadius: 5,
            }}
          >
            +{imageCount - 1}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: C.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {primary}
            {secondary && (
              <span style={{ color: C.sub, fontWeight: 500 }}> · {secondary}</span>
            )}
          </div>
          <StatusChip status={statusStr} />
        </div>
        {item.etc && (
          <div
            style={{
              fontSize: 12,
              color: C.body,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.etc}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.sub, marginTop: 1 }}>
          <span>{relativeTime(item.createDate)}</span>
          <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {statusStr === "1" && expertName
              ? `${expertName} 기사`
              : statusStr === "0"
              ? "기사 배정 대기 중"
              : statusStr === "2"
              ? "수리 완료"
              : ""}
          </span>
        </div>
      </div>
    </NavLink>
  );
}

function SectionHeader({ title, toLink, toLabel = "더보기" }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
        {title}
      </h2>
      {toLink && (
        <NavLink
          to={toLink}
          style={{
            fontSize: 13,
            color: C.greenDark,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          {toLabel} →
        </NavLink>
      )}
    </div>
  );
}

export default function NormalMainMobile() {
  const { userData } = useUser();
  const navigate = useNavigate();
  const [myMatchingList, setMyMatchingList] = useState([]);
  const [communityList, setCommunityList] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const r = await getMatchingList(userData.id, 10);
      if (r) setMyMatchingList(r[1] ?? []);
    } catch (e) {}
    try {
      const blockData = userData?.docId ? await getBlockData(userData.docId) : null;
      const r2 = await getCommunityData("", 5, null, blockData);
      if (r2) setCommunityList(r2);
    } catch (e) {}
  };

  const counts = useMemo(() => {
    const inProgress = myMatchingList.filter((m) => m.status === "0").length;
    const matched = myMatchingList.filter((m) => m.status === "1").length;
    return { total: myMatchingList.length, inProgress, matched };
  }, [myMatchingList]);

  const formatCategory = (item) => {
    const names = item?.categoryListName ?? [];
    return names.filter((n) => n && n !== "기타").join(" · ") || "기타";
  };

  return (
    <div style={{ background: C.bg, paddingBottom: 96, minHeight: "100vh" }}>
      {/* ====== Ad Banner Slot (위) ====== */}
      <div style={{ paddingTop: 16, paddingLeft: 0, paddingRight: 0 }}>
        <AdBannerSlot />
      </div>

      {/* ====== Hero ====== */}
      <section
        style={{
          margin: "16px",
          padding: "24px 20px",
          background: `linear-gradient(135deg, ${C.greenBg} 0%, #FFFFFF 100%)`,
          borderRadius: 20,
          border: `1px solid ${C.greenBorder}`,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span
            style={{
              alignSelf: "flex-start",
              padding: "3px 10px",
              borderRadius: 9999,
              background: "#fff",
              border: `1px solid ${C.greenBorder}`,
              color: C.greenDark,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            농가 대시보드
          </span>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
            {userData?.name ? `${userData.name}님, 안녕하세요` : "안녕하세요"} 👋
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: C.body, lineHeight: 1.5 }}>
            버튼 한 번이면 가까운 수리기사와 바로 연결됩니다.
          </p>
        </div>
        <NavLink
          to="/fix_request/write"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "14px 16px",
            background: C.greenDark,
            color: "#fff",
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            textDecoration: "none",
            boxShadow: `0 6px 16px ${C.green}33`,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
          새 수리요청 등록
        </NavLink>
      </section>

      {/* ====== Quick Stats ====== */}
      <section style={{ display: "flex", gap: 10, margin: "0 16px 20px" }}>
        {[
          { label: "전체", value: counts.total, fg: C.ink },
          { label: "매칭중", value: counts.inProgress, fg: C.amber800 },
          { label: "매칭완료", value: counts.matched, fg: "#1E40AF" },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              flex: 1,
              padding: "14px 8px",
              background: "#fff",
              borderRadius: 12,
              border: `1px solid ${C.line}`,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 500, marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.fg, letterSpacing: "-0.02em" }}>
              {s.value}
            </div>
          </div>
        ))}
      </section>

      {/* ====== 내 요청 목록 ====== */}
      <section
        style={{
          margin: "0 16px 16px",
          padding: 20,
          background: "#fff",
          borderRadius: 16,
          border: `1px solid ${C.line}`,
        }}
      >
        <SectionHeader title="내 요청 목록" toLink="/matching" toLabel="전체보기" />
        {myMatchingList.length === 0 ? (
          <div style={{ padding: "32px 0", textAlign: "center" }}>
            <div style={{ color: C.sub, fontSize: 13, marginBottom: 12 }}>
              아직 요청하신 내역이 없어요
            </div>
            <NavLink
              to="/fix_request/write"
              style={{
                display: "inline-block",
                padding: "8px 16px",
                background: C.greenBg,
                color: C.greenDark,
                border: `1px solid ${C.greenBorder}`,
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              첫 수리요청 등록하기
            </NavLink>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {myMatchingList.slice(0, 3).map((item, index) => (
              <MatchingCardMobile
                key={item.docId ?? index}
                item={item}
                to={`/matching/${item.docId}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* ====== 커뮤니티 ====== */}
      <section
        style={{
          margin: "0 16px 16px",
          padding: 20,
          background: "#fff",
          borderRadius: 16,
          border: `1px solid ${C.line}`,
        }}
      >
        <SectionHeader title="커뮤니티 최신 글" toLink="/community" />
        {communityList.length === 0 ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: C.sub, fontSize: 13 }}>
            작성된 글이 없습니다.
          </div>
        ) : (
          communityList.slice(0, 5).map((item, index, arr) => (
            <NavLink
              to={`/community/${item.docId}`}
              key={item.docId ?? index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 0",
                borderBottom:
                  index !== Math.min(arr.length, 3) - 1 ? `1px solid ${C.line}` : "none",
                textDecoration: "none",
                gap: 12,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: C.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {item.title}
              </div>
              <div style={{ fontSize: 12, color: C.sub, whiteSpace: "nowrap" }}>
                {moment(item.createDate).format("YY.MM.DD")}
              </div>
            </NavLink>
          ))
        )}
      </section>
      <Chatbot />
    </div>
  );
}
